import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { getSupabase } from "@/lib/supabase";
import { featuredProjects } from "@/mocks/projects";
import { useHackers } from "@/hooks/useHackers";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageMeta from "@/components/base/PageMeta";

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

const projectDescriptions: Record<number, { description: string; requirements: string[]; status: string; deliveryTime: string }> = {
  9293: {
    description: "I need someone to permanently delete a specific Instagram account that has been harassing me and posting defamatory content. The account belongs to a former colleague who has been posting false information about me for months. I have reported it to Instagram multiple times but they have taken no action. I need this account completely removed from the platform.",
    requirements: ["Must be completely untraceable", "Account must be permanently deleted", "No recovery possible", "Proof of deletion required"],
    status: "Open",
    deliveryTime: "3-5 days",
  },
  9283: {
    description: "I have a phone number and I need to find out the full identity of the person behind it. This includes their full name, current address, social media profiles, and any other available information. The number has been contacting my family member and I need to know who it belongs to for safety reasons.",
    requirements: ["Full name and address", "Social media profiles", "Employment information if available", "Complete report within 5 days"],
    status: "Open",
    deliveryTime: "5-7 days",
  },
  9384: {
    description: "I need a specific software application cracked and its license verification system bypassed. The software is a professional video editing tool that costs $299/year. I need the full version with all features unlocked including the premium plugin suite. The crack should be stable and work with future updates disabled.",
    requirements: ["Full feature unlock", "License bypass", "No watermark", "Stable and tested"],
    status: "In Progress",
    deliveryTime: "2-3 days",
  },
  9229: {
    description: "Comprehensive penetration testing required for a mid-size e-commerce platform handling over 10,000 transactions monthly. The test should cover network infrastructure, web application vulnerabilities, API security, and database security. A full report with remediation recommendations is required. This is a white-hat engagement with full authorization.",
    requirements: ["OWASP Top 10 testing", "Network vulnerability scan", "API security audit", "Detailed remediation report", "Re-test after fixes"],
    status: "Open",
    deliveryTime: "14-21 days",
  },
  9175: {
    description: "I need my university grade changed in the system. I failed one course last semester due to personal issues and this is preventing me from graduating on time. I need someone who can access the university's student information system and modify my grade from an F to at least a C without being detected.",
    requirements: ["Undetectable change", "Must persist through audits", "Quick turnaround needed", "No traces left in system logs"],
    status: "Open",
    deliveryTime: "5-10 days",
  },
  9214: {
    description: "I need access to a Telegram bot that has been spamming my community group. I want to identify the bot owner and gain control of the bot to shut it down. The bot has been flooding our group with malicious links and phishing attempts for weeks.",
    requirements: ["Bot owner identification", "Bot takeover or shutdown", "Source of automation identified", "Prevention of recurrence"],
    status: "Open",
    deliveryTime: "3-7 days",
  },
  9173: {
    description: "I suspect my girlfriend is cheating on me with multiple people. I need someone to hack into her social media accounts (Instagram, WhatsApp, Facebook Messenger) and provide me with chat logs, deleted messages, and any evidence of infidelity. I need conclusive proof before confronting her.",
    requirements: ["Access to all social media", "Chat logs extraction", "Deleted message recovery", "Complete discretion required"],
    status: "Open",
    deliveryTime: "3-5 days",
  },
  9157: {
    description: "A trading platform has frozen my funds worth $4,500 and refuses to release them. I need someone to help recover the money. The platform claims I violated their terms, but this is false. I need a hacker who can either pressure the platform into releasing the funds or extract the money through alternative means.",
    requirements: ["Fund recovery", "Platform pressure or access", "Proof of fund release", "No legal complications for client"],
    status: "Urgent",
    deliveryTime: "7-14 days",
  },
};

interface DisplayProject {
  id: number;
  title: string;
  author: string;
  authorAvatar: string;
  date: string;
  budget: string;
  category: string;
  description: string;
  status: string;
  deliveryTime?: string;
  requirements?: string[];
  paymentMethod?: string | null;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<DisplayProject | null>(null);
  const [loading, setLoading] = useState(true);
  const { hackers } = useHackers();

  useEffect(() => {
    const numericId = Number(id);
    if (!numericId) {
      setLoading(false);
      return;
    }

    const supabase = getSupabase();
    supabase
      .from("projects")
      .select("*, profiles:client_id(username, display_name)")
      .eq("id", numericId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!error && data) {
          const db = data as Record<string, unknown>;
          const profiles = db.profiles as { username?: string; display_name?: string } | null;
          setProject({
            id: db.id as number,
            title: db.title as string,
            author: profiles?.username || profiles?.display_name || "Anonymous",
            authorAvatar: "https://hackerslodge.com/wp-content/uploads/2026/01/user-150x150-1.png",
            date: formatDate(db.created_at as string),
            budget: db.budget as string,
            category: db.category as string,
            description: (db.description as string) || "No description provided.",
            status: (db.status as string) || "Open",
            deliveryTime: (db.delivery_time as string) || undefined,
            paymentMethod: (db.payment_method as string) || null,
          });
        } else {
          // Fallback to mock data
          const mockProject = featuredProjects.find((p) => p.id === numericId);
          const mockDetails = mockProject ? projectDescriptions[mockProject.id] : null;
          if (mockProject && mockDetails) {
            setProject({
              id: mockProject.id,
              title: mockProject.title,
              author: mockProject.author,
              authorAvatar: mockProject.authorAvatar,
              date: mockProject.date,
              budget: mockProject.budget,
              category: mockProject.category,
              description: mockDetails.description,
              status: mockDetails.status,
              deliveryTime: mockDetails.deliveryTime,
              requirements: mockDetails.requirements,
            });
          }
        }
        setLoading(false);
      })
      .catch(() => {
        // Fallback to mock data on error
        const mockProject = featuredProjects.find((p) => p.id === numericId);
        const mockDetails = mockProject ? projectDescriptions[mockProject.id] : null;
        if (mockProject && mockDetails) {
          setProject({
            id: mockProject.id,
            title: mockProject.title,
            author: mockProject.author,
            authorAvatar: mockProject.authorAvatar,
            date: mockProject.date,
            budget: mockProject.budget,
            category: mockProject.category,
            description: mockDetails.description,
            status: mockDetails.status,
            deliveryTime: mockDetails.deliveryTime,
            requirements: mockDetails.requirements,
          });
        }
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1117]">
        <Header />
        <main className="pt-20 md:pt-24 flex items-center justify-center min-h-[60vh]">
          <i className="fas fa-spinner fa-spin text-2xl text-[#5bc0de]" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#0d1117]">
        <Header />
        <main className="pt-20 md:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="fas fa-folder-open text-6xl text-gray-300 mb-4 block" />
            <h1 className="text-2xl font-light text-gray-200 mb-2">Project Not Found</h1>
            <p className="text-sm text-gray-500 mb-6">This project does not exist or has been removed.</p>
            <Link to="/projects-listing" className="inline-block bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold px-6 py-2.5 rounded transition-all whitespace-nowrap">
              Browse All Projects
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const similarProjects = featuredProjects
    .filter((p) => p.id !== project.id && p.category === project.category)
    .slice(0, 3);

  const recommendedHackers = hackers.slice(0, 4);

  const projectSchema = project ? {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: `${siteUrl}/project/${project.id}`,
    name: `${project.title} — Hackers Lodge Project`,
    description: project.description?.substring(0, 160) || `Project: ${project.title}. Budget: ${project.budget}. Category: ${project.category}.`,
  } : null;

  const breadcrumbSchema = project ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${siteUrl}/` },
      { "@type": "ListItem", position: 2, name: "Projects", item: `${siteUrl}/projects-listing` },
      { "@type": "ListItem", position: 3, name: project.title },
    ],
  } : null;

  const getPaymentIcon = (method: string | null | undefined): string => {
    switch (method) {
      case "crypto": return "fa-bitcoin";
      case "escrow": return "fa-shield-halved";
      case "wire": return "fa-building-columns";
      default: return "fa-credit-card";
    }
  };

  const formatPaymentMethod = (method: string | null | undefined): string => {
    if (!method) return "Not specified";
    if (method === "crypto") return "Cryptocurrency";
    if (method === "escrow") return "Escrow Service";
    if (method === "wire") return "Wire Transfer";
    return method;
  };

  return (
    <div className="min-h-screen bg-[#161b22]">
      {project && (
        <PageMeta
          title={`${project.title} — Cybersecurity Project | Hackers Lodge`}
          description={`${project.title}: ${project.description?.substring(0, 150) || `Budget: ${project.budget}, Category: ${project.category}, Status: ${project.status}`}. Posted by ${project.author} on Hackers Lodge.`}
          keywords={`${project.category}, hacking project, cybersecurity project, ethical hacking, ${project.category.toLowerCase()}`}
          canonicalUrl={`${siteUrl}/project/${project.id}`}
          ogType="website"
          schema={projectSchema}
          breadcrumbSchema={breadcrumbSchema}
        />
      )}
      <Header />
      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto py-4">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Projects", to: "/projects-listing" }, { label: project?.title || "..." }]} />
        </div>
        {/* Project Header */}
        <section className="bg-[#2c3e50] py-8 md:py-12">
          <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="bg-[#5bc0de]/20 text-[#5bc0de] px-2.5 py-0.5 rounded text-xs font-semibold">{project.category}</span>
              <span className={`px-2.5 py-0.5 rounded text-xs font-semibold ${
                project.status === "Urgent" ? "bg-red-500/20 text-red-400" :
                project.status === "In Progress" ? "bg-yellow-500/20 text-yellow-400" :
                "bg-green-500/20 text-green-400"
              }`}>{project.status}</span>
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-white mb-4">{project.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
              <span className="flex items-center gap-2">
                <img src={project.authorAvatar} alt="" className="w-6 h-6 rounded-full" />
                Posted by <strong className="text-white">{project.author}</strong>
              </span>
              <span><i className="far fa-calendar mr-1" />{project.date}</span>
              {project.deliveryTime && <span><i className="far fa-clock mr-1" />{project.deliveryTime}</span>}
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left - Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Budget & Actions */}
              <div className="bg-[#0d1117] rounded border border-gray-700 p-5 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Budget</span>
                  <p className="text-2xl font-bold text-[#5cb85c]">{project.budget}</p>
                </div>
                <div className="flex gap-3">
                  <button className="border border-gray-600 hover:bg-[#161b22] text-gray-300 text-sm font-semibold px-4 py-2.5 rounded transition-all whitespace-nowrap">
                    <i className="far fa-bookmark mr-1" />Save
                  </button>
                </div>
              </div>

              {/* Description */}
              <div className="bg-[#0d1117] rounded border border-gray-700 p-5 md:p-6">
                <h2 className="text-lg font-semibold text-gray-200 mb-3">Project Description</h2>
                <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{project.description}</p>
              </div>

              {/* Requirements */}
              {project.requirements && project.requirements.length > 0 && (
                <div className="bg-[#0d1117] rounded border border-gray-700 p-5 md:p-6">
                  <h2 className="text-lg font-semibold text-gray-200 mb-3">Requirements</h2>
                  <ul className="space-y-2">
                    {project.requirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                        <i className="fas fa-check-circle text-[#5cb85c] mt-0.5 flex-shrink-0" />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Similar Projects */}
              {similarProjects.length > 0 && (
                <div className="bg-[#0d1117] rounded border border-gray-700 p-5 md:p-6">
                  <h2 className="text-lg font-semibold text-gray-200 mb-4">
                    Similar Projects
                    <span className="text-sm font-normal text-gray-400 ml-2">({project.category})</span>
                  </h2>
                  <ul className="divide-y divide-gray-700">
                    {similarProjects.map((sp) => (
                      <li key={sp.id} className="py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <img src={sp.authorAvatar} alt="" className="w-8 h-8 rounded-full flex-shrink-0" />
                          <Link to={`/project/${sp.id}`} className="text-sm text-gray-200 hover:text-[#5bc0de] truncate font-medium">{sp.title}</Link>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                          <span className="text-xs text-gray-500 hidden sm:inline">{sp.date}</span>
                          <span className="text-sm font-semibold text-gray-300">{sp.budget}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Right - Sidebar */}
            <div className="space-y-5">
              {/* Project Info */}
              <div className="bg-[#0d1117] rounded border border-gray-700 p-5">
                <h3 className="text-sm font-semibold text-gray-200 mb-4">Project Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Status</span>
                    <span className={`text-sm font-semibold ${
                      project.status === "Urgent" ? "text-red-500" :
                      project.status === "In Progress" ? "text-yellow-500" :
                      "text-green-500"
                    }`}>{project.status}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Budget</span>
                    <span className="text-sm font-semibold text-gray-300">{project.budget}</span>
                  </div>
                  {project.deliveryTime && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Delivery Time</span>
                      <span className="text-sm font-semibold text-gray-300">{project.deliveryTime}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Posted</span>
                    <span className="text-sm font-semibold text-gray-300">{project.date}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Category</span>
                    <span className="text-sm font-semibold text-[#5bc0de]">{project.category}</span>
                  </div>
                  {project.paymentMethod && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Payment</span>
                      <span className="text-sm font-semibold text-gray-300 flex items-center gap-1.5">
                        <i className={`fas ${getPaymentIcon(project.paymentMethod)} text-xs`} />
                        {formatPaymentMethod(project.paymentMethod)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Recommended Hackers */}
              <div className="bg-[#0d1117] rounded border border-gray-700 p-5">
                <h3 className="text-sm font-semibold text-gray-200 mb-4">Recommended Hackers</h3>
                <div className="space-y-3">
                  {recommendedHackers.map((h) => (
                    <Link key={h.id} to={`/hacker/${h.id}`} className="flex items-center gap-3 py-2 hover:bg-[#161b22] -mx-2 px-2 rounded transition-colors">
                      <img src={h.avatar} alt={h.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-200 truncate">
                          {h.verified && (
                            <i className="fas fa-check-circle text-[#5bc0de] text-xs mr-1" />
                          )}
                          {h.name}
                        </p>
                        <p className="text-xs text-gray-400">{h.specialty} &middot; {h.rate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to="/projects-listing"
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors"
              >
                <i className="fas fa-arrow-left" /> Back to Projects Listing
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}